import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INPUT_CLS = 'bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] rounded-xl px-4 py-3 text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500 focus:border-[#FF451A]/50 focus:ring-2 focus:ring-[#FF451A]/20 transition-all';
const LABEL_CLS = 'font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 block mb-2';

export const Register = () => {
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'creator',
    exclusivity_type: 'marketplace',
    company_name: '',
    website: '',
    industry: '',
    monthly_budget: '',
    contact_person: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        password: form.password,
        name: form.name,
        role: form.role,
      };
      if (form.role === 'creator') {
        payload.exclusivity_type = form.exclusivity_type;
      }
      if (form.role === 'brand') {
        payload.company_name = form.company_name;
        payload.website = form.website || null;
        payload.industry = form.industry || null;
        payload.monthly_budget = form.monthly_budget ? parseFloat(form.monthly_budget) : null;
        payload.contact_person = form.contact_person || form.name;
        payload.phone = form.phone || null;
      }
      const { data } = await axios.post(`${API_URL}/api/auth/register`, payload, { withCredentials: true });
      window.location.assign(data.role === 'brand' ? '/brand/dashboard' : '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white">
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-[#0A0B0D]">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <span className="text-3xl">🏴‍☠️</span>
              <span className="font-display text-2xl tracking-tight">
                <span className="text-[#0F0F0F] dark:text-white">Vy</span><span className="text-[#FF451A] italic">vre</span>
              </span>
            </div>
            <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-3 leading-tight">Create account.</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500">Join the premium creator economy OS</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 rounded-xl text-sm" data-testid="register-error-message">
                {error}
              </div>
            )}

            <div>
              <label className={LABEL_CLS}>I am a</label>
              <Select value={form.role} onValueChange={(v) => update('role', v)}>
                <SelectTrigger className={INPUT_CLS} data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="creator">Creator</SelectItem>
                  <SelectItem value="brand">Brand</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className={LABEL_CLS}>Full Name</label>
              <Input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="John Doe"
                required
                className={INPUT_CLS}
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="you@example.com"
                required
                className={INPUT_CLS}
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Password</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => update('password', e.target.value)}
                placeholder="••••••••"
                required
                className={INPUT_CLS}
                data-testid="register-password-input"
              />
            </div>

            {form.role === 'creator' && (
              <div>
                <label className={LABEL_CLS}>Creator Type</label>
                <Select value={form.exclusivity_type} onValueChange={(v) => update('exclusivity_type', v)}>
                  <SelectTrigger className={INPUT_CLS} data-testid="register-exclusivity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketplace">Marketplace (open to brand deals)</SelectItem>
                    <SelectItem value="exclusive">Exclusive (managed by StrawHat talent team)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.role === 'brand' && (
              <>
                <div>
                  <label className={LABEL_CLS}>Company Name</label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => update('company_name', e.target.value)}
                    placeholder="Nike India"
                    required
                    className={INPUT_CLS}
                    data-testid="register-company-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLS}>Industry</label>
                    <Input
                      value={form.industry}
                      onChange={(e) => update('industry', e.target.value)}
                      placeholder="Fashion"
                      className={INPUT_CLS}
                      data-testid="register-industry-input"
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Monthly Budget (₹)</label>
                    <Input
                      type="number"
                      value={form.monthly_budget}
                      onChange={(e) => update('monthly_budget', e.target.value)}
                      placeholder="500000"
                      className={INPUT_CLS}
                      data-testid="register-budget-input"
                    />
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-tactile btn-tactile-primary w-full px-6 py-3.5 rounded-md disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FF451A] font-medium hover:text-white transition-colors" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden" style={{ background: 'radial-gradient(circle at 70% 30%, #FF451A 0%, #1a1a1a 40%, #050505 100%)' }}>
        <div className="absolute inset-0 bg-[url('https://static.prod-images.emergentagent.com/jobs/9de54d98-d977-4d52-98c6-87def6f7dbb6/images/270719a556826637cdb21fff7e6a54d54775e71246a523c63143781c80b4f5f3.png')] bg-cover bg-center opacity-40 mix-blend-screen"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-transparent to-[#FF451A]/30"></div>
        <div className="text-center text-[#0F0F0F] dark:text-white relative z-10 px-12">
          <h2 className="font-display text-7xl tracking-tight leading-[0.95] mb-4">
            Vy<span className="italic text-[#FF451A]">vre</span>
            <br /><span className="font-display text-3xl text-[#6B6B6B] dark:text-zinc-400 italic">Creator Intelligence.</span>
          </h2>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-400 mb-8">The premium talent operating system</p>
          <div className="space-y-3 text-left max-w-sm mx-auto font-body text-zinc-300">
            <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Brand Discovery Engine</div>
            <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />AI Creator Matching</div>
            <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Multi-tenant Workflows</div>
            <div className="flex items-center gap-3"><div className="w-1.5 h-1.5 rounded-full bg-[#FF451A]" />Invoicing in ₹ INR</div>
          </div>
        </div>
      </div>
    </div>
  );
};
